import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DKButton,
    DKLabel,
    DKInput,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    DKIcons,
    showLoader,
    removeLoader,
    showAlert,
    showToast,
    TOAST_TYPE,
} from 'deskera-ui-library';
import CustomSectionService from '../../services/CustomSectionService';
import ic_replay from '../../assets/icons/ic_replay.svg';
import EmployeeConfigService from '../../services/EmployeeConfigService';

function DropdownManagement(props) {
    const { t } = useTranslation();

    const [dropdownFields, setDropdownFields] = useState([]);
    const [selectedField, setSelectedField] = useState(null);
    const [options, setOptions] = useState([]);
    const [newOptionName, setNewOptionName] = useState('');

    useEffect(() => {
        getDropdownFields();
    }, []);

    const getDropdownFields = async () => {
        try {
            showLoader();
            const response = await CustomSectionService.getDropdownFields();
            if (Array.isArray(response?.customFields)) {
                setDropdownFields(response.customFields);
            }
            removeLoader();
        } catch (error) {
            setDropdownFields([]);
            removeLoader();
            showToast('Internal server error, please try again later.', TOAST_TYPE.FAILURE);
        }
    };

    const getIsOptionChanged = (selectedField, options) => {
        if (Array.isArray(selectedField?.options) && Array.isArray(options)) {
            const sorted1 = [...selectedField.options].sort((a, b) => a.id - b.id);
            const sorted2 = [...options].sort((a, b) => a.id - b.id);
    
            // If any option is marked deleted
            if (sorted2.some(option => option.deleted)) {
                return true;
            }
    
            // If lengths differ, options have changed
            if (sorted1.length !== sorted2.length) {
                return true;
            }
    
            // Compare each element
            return !sorted1.every((option, idx) =>
                option.id === sorted2[idx].id &&
                option.name === sorted2[idx].name
            );
        }
    
        return false;
    };

    const showUnsavedChangesAlert = (field, onContinue) => {
        const actions = [
            {
                title: 'Go Back',
                className: 'bg-app text-white mr-m',
                onClick: () => {},
            },
            {
                title: 'Continue',
                className: 'border-m',
                onClick: () => {
                    onContinue(field);
                },
            },
        ];
        showAlert('Alert', 'You have unsaved changes. Click Go Back to make sure nothing is lost.', actions);
    };

    const onFieldSelectionChange = (field) => {
        if (selectedField?.id === field?.id) {
            return;
        }

        if (getIsOptionChanged(selectedField, options)) {
            showUnsavedChangesAlert(field, setSelectedFieldAndOptions);
            return;
        }

        setSelectedFieldAndOptions(field);
    };

    const setSelectedFieldAndOptions = (field) => {
        setSelectedField(field);
        setOptions(field.options);
        setNewOptionName('');
    };

    const onAddNewOption = () => {
        if (typeof newOptionName === 'string' && newOptionName.length > 0) {
            if (options.some((o) => o.name.toLowerCase() === newOptionName.toLowerCase())) {
                showAlert('Alert', 'Option with similar name already exists.');
                return;
            }

            setOptions([
                {
                    id: Math.random().toString(16).slice(2, 10),
                    name: newOptionName,
                    added: true,
                },
                ...options,
            ]);

            setNewOptionName('');
        }
    };

    const onDeleteOption = (option) => {
        if (option.added) {
            setOptions(options.filter((o) => o.id !== option.id));
        } else {
            setOptions(
                options.map((o) => {
                    const newOption = { ...o };
                    if (o.id === option.id) {
                        newOption.deleted = true;
                    }

                    return newOption;
                })
            );
        }
    };

    const onRevertDeleteOption = (option) => {
        setOptions(
            options.map((o) => {
                const newOption = { ...o };
                if (o.id === option.id) {
                    newOption.deleted = false;
                }

                return newOption;
            })
        );
    };

    const onSaveSuccess = (updatedCustomField) => {
        setSelectedFieldAndOptions(updatedCustomField);
        EmployeeConfigService.refreshDropdownOptionsInEmployeeConfig(updatedCustomField);

        setDropdownFields(dropdownFields.map((dropdownField) => {
            const newDropdownField = { ...dropdownField };
            if (newDropdownField.id === updatedCustomField.id) {
                newDropdownField.options = updatedCustomField.options;
            }
            return newDropdownField;
        }));
    }

    const onSave = () => {
        if (!selectedField) {
            showAlert('Alert', 'Please select dropdown to add options.');
            return;
        }

        if (!getIsOptionChanged(selectedField, options)) {
            showAlert('Alert', 'There are no changes in the options to save.');
            return;
        }

        const payload = {};
        const added = options.filter((o) => o.added);
        const deleted = options.filter((o) => o.deleted);
        if (added.length > 0 || deleted.length > 0) {
            if (added.length > 0) {
                payload.add = {
                    id: selectedField.id,
                    options: added.map((o) => o.name),
                };
            }

            if (deleted.length > 0) {
                payload.delete = {
                    id: selectedField.id,
                    options: deleted.map((o) => o.id),
                };
            }

            showLoader('Updating... Please wait');
            CustomSectionService.updateDropdownOptions(payload).then(
                (response) => {
                    if (response?.customField) {
                        onSaveSuccess(response.customField)
                    }
                    removeLoader();
                },
                (error) => {
                    removeLoader();
                    showToast('Internal server error, please try again later.', TOAST_TYPE.FAILURE);
                }
            );
        }
    };

    const onClose = () => {
        if (getIsOptionChanged(selectedField, options)) {
            showUnsavedChangesAlert(null, props.onClose);
            return;
        }

        props.onClose();
    };

    const renderOptionName = (name, className) => {
        return <DKLabel text={name} className={className} />;
    };

    return (
        <div className="transparent-background">
            <div className="popup-window" style={{ padding: 0, overflow: 'hidden', maxWidth: '500px', boxShadow: 'none' }}>
                <div className="parent-width" style={{ height: '60vh' }}>
                    <div className="row p-h-xl pt-xl pb-m">
                        <DKLabel text={t('Dropdown Management')} className="fs-l fw-b" />
                        <div className="ml-auto d-flex">
                            <DKButton
                                title={t('CLOSE')}
                                className="border-radius-m bg-gray1 border-m"
                                onClick={() => {
                                    onClose();
                                }}
                            />
                            <DKButton
                                title={t('SAVE')}
                                className="bg-app text-white ml-r"
                                onClick={() => {
                                    onSave();
                                }}
                            />
                        </div>
                    </div>
                    <div className="row p-h-xl pb-m">
                        <DKInput
                            title={'Select dropdown to manage'}
                            type={INPUT_TYPE.DROPDOWN}
                            direction={INPUT_VIEW_DIRECTION.VERTICAL}
                            value={selectedField}
                            formatter={(option) => {
                                return option?.name || option;
                            }}
                            dropdownConfig={{
                                allowSearch: true,
                                searchableKey: 'name',
                                data: dropdownFields,
                                renderer: (index, obj) => {
                                    return <DKLabel text={obj.name} />;
                                },
                                onSelect: (index, value) => {},
                            }}
                            onChange={(value) => {
                                onFieldSelectionChange(value);
                            }}
                            onClick={() => {}}
                        />
                    </div>
                    <div className="p-h-xl">
                        <div className="row justify-content-between pl-r pr-xs p-v-m bg-gray1 z-index-2">
                            <DKLabel
                                text={'Options'}
                                className="fw-m "
                                style={{
                                    top: 0,
                                    left: 0,
                                    position: 'sticky',
                                }}
                            />
                        </div>
                    </div>
                    <div className="pb-xl" style={{ overflow: 'hidden', height: 'calc(100% - 198px)' }}>
                        <div className="p-h-xl" style={{ height: 'calc(100% - 2px)' }}>
                            <div className="parent-height border-s border-radius-s custom-scroll" style={{ overflowY: 'scroll' }}>
                                {selectedField && (
                                    <div className="row justify-content-between pl-m pr-s p-v-m">
                                        <DKInput
                                            className="mr-r"
                                            type={'text'}
                                            direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                            value={newOptionName}
                                            onChange={(value) => {
                                                setNewOptionName(value);
                                            }}
                                        />
                                        <DKButton
                                            icon={DKIcons.ic_add}
                                            className="ic-s cursor-pointer"
                                            disabled={newOptionName.length == 0}
                                            style={{ opacity: newOptionName.length == 0 ? 0.4 : 0.75 }}
                                            onClick={() => {
                                                onAddNewOption();
                                            }}
                                        />
                                    </div>
                                )}
                                {options.map((option) => (
                                    <div className="row justify-content-between pl-r pr-s p-v-m">
                                        {option.added && renderOptionName(option.name, 'fw-b text-app')}
                                        {option.deleted && renderOptionName(option.name, 'fw-b text-red text-cut')}
                                        {!option.added && !option.deleted && renderOptionName(option.name, '')}
                                        {option.added && (
                                            <DKButton
                                                icon={DKIcons.ic_delete}
                                                className="ic-s cursor-pointer"
                                                style={{ opacity: 0.75 }}
                                                onClick={() => {
                                                    onDeleteOption(option);
                                                }}
                                            />
                                        )}
                                        {option.deleted && (
                                            <DKButton
                                                icon={ic_replay}
                                                className="ic-s cursor-pointer"
                                                style={{ opacity: 0.65, transform: 'scale(1.15)' }}
                                                onClick={() => {
                                                    onRevertDeleteOption(option);
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DropdownManagement;
